import { apiClient } from './client';
import type { AufgabenEntityType } from './aufgaben';

export type AufgabeStatus = 'offen' | 'erledigt';
export type AufgabeStatusFilter = 'offen' | 'erledigt' | 'all';
export type AufgabeDue = 'all' | 'overdue' | 'today' | 'week';

export interface Assignee {
  id: number;
  full_name: string;
  email?: string | null;
}

export interface Aufgabe {
  id: number;
  city_id: number;
  entity_type: AufgabenEntityType;
  entity_id: number;
  title: string;
  description: string | null;
  bearbeiter: Assignee | null;
  faelligkeit: string | null;
  status: AufgabeStatus;
  erledigt_at: string | null;
  erledigt_by: Assignee | null;
  created_by: Assignee | null;
  created_at: string;
}

export interface AufgabeListItem extends Aufgabe {
  entity_title: string;
  entity_subtitle: string | null;
}

export interface AufgabeListResponse {
  items: AufgabeListItem[];
  total: number;
}

export interface AufgabenCount {
  offen: number;
  overdue: number;
}

export interface ZustaendigkeitItem {
  entity_type: AufgabenEntityType;
  entity_id: number;
  title: string;
  subtitle: string | null;
  open_tasks: number;
}

export interface AufgabeCreateBody {
  entity_type: AufgabenEntityType;
  entity_id: number;
  title: string;
  description?: string | null;
  bearbeiter_user_id?: number | null;
  faelligkeit?: string | null;
  also_set_zustaendig?: boolean;
}

export interface AufgabeUpdateBody {
  title?: string;
  description?: string | null;
  bearbeiter_user_id?: number | null;
  faelligkeit?: string | null;
}

export interface TaskSuggestion {
  title: string;
  frist_tage: number | null;
  faelligkeit: string | null;
  already_open: boolean;
}

export interface TaskSuggestionsResponse {
  status_label: string | null;
  suggestions: TaskSuggestion[];
}

export const aufgabeApi = {
  listMine: async (
    status: AufgabeStatusFilter = 'offen',
    type?: AufgabenEntityType,
    due: AufgabeDue = 'all',
  ): Promise<AufgabeListResponse> => {
    const { data } = await apiClient.get('/aufgaben/', {
      params: { status, ...(type ? { type } : {}), ...(due !== 'all' ? { due } : {}) },
    });
    return data;
  },

  count: async (): Promise<AufgabenCount> => {
    const { data } = await apiClient.get<AufgabenCount>('/aufgaben/count');
    return data;
  },

  forEntity: async (entityType: AufgabenEntityType, entityId: number): Promise<Aufgabe[]> => {
    const { data } = await apiClient.get('/aufgaben/for-entity', {
      params: { entity_type: entityType, entity_id: entityId },
    });
    return data;
  },

  zustaendig: async (): Promise<ZustaendigkeitItem[]> => {
    const { data } = await apiClient.get('/aufgaben/zustaendig');
    return data;
  },

  suggestions: async (entityType: AufgabenEntityType, entityId: number): Promise<TaskSuggestionsResponse> => {
    const { data } = await apiClient.get('/aufgaben/suggestions', {
      params: { entity_type: entityType, entity_id: entityId },
    });
    return data;
  },

  create: async (body: AufgabeCreateBody): Promise<Aufgabe> => {
    const { data } = await apiClient.post('/aufgaben/', body);
    return data;
  },

  update: async (id: number, body: AufgabeUpdateBody): Promise<Aufgabe> => {
    const { data } = await apiClient.patch(`/aufgaben/${id}`, body);
    return data;
  },

  complete: async (id: number): Promise<Aufgabe> => {
    const { data } = await apiClient.post(`/aufgaben/${id}/erledigt`);
    return data;
  },

  reopen: async (id: number): Promise<Aufgabe> => {
    const { data } = await apiClient.post(`/aufgaben/${id}/offen`);
    return data;
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/aufgaben/${id}`);
  },
};
